import { Field, Form, Formik, FormikHelpers } from 'formik'
import moment from 'moment'
import React, { FC, ReactElement, useCallback, useMemo, useState } from 'react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import Button from '../../components/button'
import Input from '../../components/input'
import SelectInput from '../../components/selectInput'
import SocialButton, { SocialLoginSuccess } from '../../components/socialButton'
import config from '../../config'
import { useAlertHandler } from '../../hooks/alertHandler'
import { useAuth } from '../../hooks/auth'
import { createErrorResponse } from '../../utils/createError'
import occupationList from '../../utils/occupationList'
import { RoutesEnum } from '../../utils/routesEnum'
import RegisterSkeleton from './skeleton'
import { SocialButtons } from './style'

type RegisterProps = {
  withAccessCode?: boolean
}

const initialValues2 = {
  name: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  zipCode: '',
  occupation: '',
  birthDate: '',
  accessCode: ''
}

type RegisterSocialToken = {
  type: 'google' | 'facebook'
  accessToken: string
}

const Register: FC<RegisterProps> = ({ withAccessCode }): ReactElement => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { registerParams, registerSocial, validateEmail } = useAuth()
  const [registerSocialVia, setRegisterSocialVia] = useState(null)
  const [registerSocialToken, setRegisterSocialToken] = useState<RegisterSocialToken | null>(null)
  const { showError } = useAlertHandler()
  const history = useHistory()

  const initialValues = useMemo(() => {
    return { ...initialValues2, ...registerParams }
  }, [registerParams])

  const schema = useMemo(() => {
    if (registerSocialToken) {
      return yup.object().shape({
        phone: yup.string().min(11, 'Celular inválido'),
        zipCode: yup.string().min(8, 'CEP inválido').required('CEP é obrigatório')
      })
    }

    return yup.object().shape({
      name: yup.string().min(3, 'Nome inválido')
        .matches(/[A-z]*\s[A-z]{1,}/, 'Nome e sobrenome são obrigatórios')
        .required('Nome obrigatório'),
      email: yup.string().email('Email inválido').required('Email obrigatório'),
      password: yup.string().min(6, 'Senha minimia 6 caracteres')
        .required('A senha é obrigatória'),
      confirmPassword: yup.string()
        .oneOf([yup.ref('password'), null], 'As senhas devem ser iguais')
        .required('A confirmação de senha é obrigatória'),
      phone: yup.string().min(11, 'Celular inválido'),
      zipCode: yup.string().min(8, 'CEP inválido').required('CEP é obrigatório'),
      birthDate: yup.string().min(10, 'Data de nascimento inválida')
        .required('Data de nascimento é obrigatório')
    })
  }, [registerSocialToken])

  const submit = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>
  ): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      const captcha = executeRecaptcha ? await executeRecaptcha() : undefined

      if (registerSocialToken) {
        await registerSocial({
          ...registerSocialToken,
          phone: values.phone,
          occupation: values.occupation,
          code: values.accessCode || undefined
        })
        history.push(RoutesEnum.PROCESS_EXAMS)
      } else {
        const token = await validateEmail({
          email: values.email,
          phone: values.phone,
          name: values.name,
          password: values.password,
          zipCode: values.zipCode,
          occupation: values.occupation,
          birthDate: moment(values.birthDate, 'DD/MM/YYYY').toDate(),
          code: values.accessCode || undefined
        }, captcha)
        history.push(RoutesEnum.REGISTER_VERIFICATION_CODE.replace(':validationChecker', token))
      }
    } catch { }
    helpers.setSubmitting(false)
  }

  const handleSocialRegister = useCallback((type) => {
    return async (res: SocialLoginSuccess) => {
      setRegisterSocialVia(type)
      setRegisterSocialToken({
        type,
        accessToken: type === 'google' ? res._token.idToken || '' : res._token.accessToken
      })
      setRegisterSocialVia(null)
    }
  }, [setRegisterSocialVia])

  const handleSocialRegisterFailure = useCallback(() => {
    if (registerSocialVia) {
      setRegisterSocialVia(null)
      setRegisterSocialToken(null)
      const error = createErrorResponse('Não conseguimos realizar o cadatro social.')
      showError(error)
    }
  }, [registerSocialVia, setRegisterSocialToken, setRegisterSocialVia, showError])

  return (
    <RegisterSkeleton>
      {!navigator.userAgent.includes('Prerender') && (
        <Formik
          validationSchema={schema}
          initialValues={initialValues}
          onSubmit={submit}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              <SocialButtons>
                <SocialButton
                  appId={config.FACEBOOK_APP_ID}
                  provider='facebook'
                  type='facebook'
                  scope='email'
                  autoLogin={false}
                  isLoading={registerSocialVia === 'facebook'}
                  disabled={registerSocialVia === 'google'}
                  onLoginSuccess={handleSocialRegister('facebook')}
                  onLoginFailure={handleSocialRegisterFailure}
                />
                <SocialButton
                  appId={config.GOOGLE_CLIENT_ID}
                  provider='google'
                  type='google'
                  autoLogin={false}
                  isLoading={registerSocialVia === 'google'}
                  disabled={registerSocialVia === 'facebook'}
                  onLoginSuccess={handleSocialRegister('google')}
                  onLoginFailure={handleSocialRegisterFailure}
                />
              </SocialButtons>
              {!registerSocialToken && (
                <>
                  <Field
                    component={Input}
                    name='name'
                    label='Nome'
                  />
                  <Field
                    component={Input}
                    mask='99/99/9999'
                    name='birthDate'
                    label='Data de nascimento'
                    type='tel'
                  />
                  <Field
                    component={Input}
                    name='email'
                    label='Email'
                    type='email'
                  />
                </>
              )}
              <Field
                component={Input}
                name='phone'
                label='Celular'
                mask='(99) 99999-9999'
                type='tel'
              />
              {!registerSocialToken && (
                <>
                  <Field
                    component={Input}
                    name='password'
                    label='Senha'
                    type='password'
                  />
                  <Field
                    component={Input}
                    name='confirmPassword'
                    label='Confirmar senha'
                    type='password'
                  />
                </>
              )}
              <Field
                component={Input}
                name='zipCode'
                mask='99999-999'
                label='CEP'
                type='tel'
              />
              <Field
                component={SelectInput}
                name='occupation'
                label='Profissão'
                options={occupationList.map(occupation => ({ label: occupation, value: occupation }))}
              />
              {withAccessCode && (
                <Field
                  component={Input}
                  name='accessCode'
                  label='Código de acesso'
                />
              )}
              <div className='buttons'>
                <Button
                  isLoading={isSubmitting}
                  block
                  disabled={!isValid}
                  type='submit'
                >
                  CADASTRAR
                </Button>
                <br />
                <Button
                  to={RoutesEnum.LOGIN}
                  block
                  variant='link'
                >
                  Quer fazer login? Clique aqui
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </RegisterSkeleton>
  )
}

const RegisterWithReCaptcha: FC<RegisterProps> = (props): ReactElement => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.RECAPTCHA_TOKEN}>
      <Register {...props} />
    </GoogleReCaptchaProvider>
  )
}

export default RegisterWithReCaptcha
